import { TZDate } from '@date-fns/tz';

/** Zona horaria en la que opera el negocio. Todo "día" de negocio se calcula aquí. */
export const ZONA_NEGOCIO = 'America/Mexico_City';

/** Rango de años compatible con helpers que avanzan al siguiente día y conservan `YYYY-MM-DD`. */
export const ANIO_MINIMO_DIA_NEGOCIO = 1000;
export const ANIO_MAXIMO_DIA_NEGOCIO = 9998;

const formatoDia = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONA_NEGOCIO,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const formatoHora = new Intl.DateTimeFormat('en-GB', {
  timeZone: ZONA_NEGOCIO,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Día de negocio (`YYYY-MM-DD`) al que pertenece un instante. */
export function diaNegocio(fecha: Date): string {
  return formatoDia.format(fecha);
}

function partes(dia: string): [number, number, number] {
  const coincide = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia);
  if (!coincide) throw new RangeError(`Día inválido: ${dia}`);
  return [Number(coincide[1]), Number(coincide[2]), Number(coincide[3])];
}

/** Construye un instante a partir de un día de negocio y una hora local `HH:mm`. */
export function instanteLocal(dia: string, hora: string): Date {
  const [a, m, d] = partes(dia);
  const [h = 0, min = 0] = hora.split(':').map(Number);
  return new Date(new TZDate(a, m - 1, d, h, min, 0, ZONA_NEGOCIO).getTime());
}

/** Instante (UTC) en que comienza el día de negocio. */
export function inicioDiaNegocio(dia: string): Date {
  return instanteLocal(dia, '00:00');
}

/** Instante (UTC) en que termina el día de negocio (exclusivo). */
export function finDiaNegocio(dia: string): Date {
  return inicioDiaNegocio(sumarDias(dia, 1));
}

/** Hora local `HH:mm` de un instante. */
export function horaLocal(fecha: Date): string {
  return formatoHora.format(fecha);
}

/**
 * Hora `HH:mm` (24 h, como se guarda y se valida) en formato de 12 horas para mostrar:
 * `'14:30'` → `'2:30 p.m.'`. Se arma a mano para verse igual en navegador, Node y Hermes.
 */
export function formatoHora12(hora: string): string {
  const coincide = /^(\d{1,2}):(\d{2})/.exec(hora);
  if (!coincide) return hora;
  const h = Number(coincide[1]);
  return `${h % 12 || 12}:${coincide[2]} ${h < 12 ? 'a.m.' : 'p.m.'}`;
}

/**
 * Lee una hora escrita en 24 h (`'14:30'`) o en 12 h (`'2:30 pm'`, `'2:30 p. m.'`)
 * y la devuelve como `HH:mm`; `null` si no es una hora válida.
 */
export function parsearHora(texto: string): string | null {
  const coincide = /^\s*(\d{1,2}):([0-5]\d)\s*(?:([ap])\.?\s*m\.?)?\s*$/i.exec(texto);
  if (!coincide) return null;
  let h = Number(coincide[1]);
  const periodo = coincide[3]?.toLowerCase();
  if (periodo) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (periodo === 'p' ? 12 : 0);
  } else if (h > 23) {
    return null;
  }
  return `${String(h).padStart(2, '0')}:${coincide[2]}`;
}

/** Hora local del negocio de un instante, en formato de 12 horas (`'11:00 a.m.'`). */
export function hora12(fecha: Date): string {
  return formatoHora12(horaLocal(fecha));
}

export function sumarDias(dia: string, n: number): string {
  const [a, m, d] = partes(dia);
  return new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Día de la semana: 0 = domingo … 6 = sábado. */
export function diaSemana(dia: string): number {
  const [a, m, d] = partes(dia);
  return new Date(Date.UTC(a, m - 1, d)).getUTCDay();
}

/** Primer día de la semana que contiene `dia`. `inicio` 1 = lunes, 0 = domingo. */
export function inicioSemana(dia: string, inicio: 0 | 1 = 1): string {
  const retroceso = (diaSemana(dia) - inicio + 7) % 7;
  return sumarDias(dia, -retroceso);
}

/** Diferencia en días entre dos días de negocio (`b - a`). */
export function diferenciaDias(a: string, b: string): number {
  const [a1, m1, d1] = partes(a);
  const [a2, m2, d2] = partes(b);
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86_400_000);
}

export function esDiaValido(dia: string): boolean {
  const coincide = /^(\d{4})-\d{2}-\d{2}$/.exec(dia);
  if (!coincide) return false;
  const anio = Number(coincide[1]);
  if (anio < ANIO_MINIMO_DIA_NEGOCIO || anio > ANIO_MAXIMO_DIA_NEGOCIO) return false;
  const fecha = new Date(`${dia}T00:00:00.000Z`);
  return !Number.isNaN(fecha.getTime()) && fecha.toISOString().slice(0, 10) === dia;
}
